import type { ColumnDataType, ColumnUpdater } from '@blocksuite/affine-model';
import { dispatchDatabaseFileCellIndexReviewCopy } from '@blocksuite/affine-shared/services';
import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import {
  assertDatabaseColumnShapeWithinLimits,
  DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
  type DataSourceBase,
  type PropertyDuplicateOptions,
  type PropertyMetaConfig,
  type TypeInstance,
} from '@blocksuite/data-view';
import { propertyPresets } from '@blocksuite/data-view/property-presets';
import {
  assertDatabaseColumnOptionsWithinLimit,
  type DatabaseCellColumn,
  databaseColumnCellKey,
  DatabaseColumnCellKeyCollisionError,
  databaseColumnDeletedKeys,
  databaseColumnOptionCount,
  databaseTypeChangeReadsTableValues,
  deletedColumnsAfterTypeChange,
  FILE_INDEX_MANAGED_BY,
  isDatabaseLimitError,
  isTemporalProfileViewType,
  normalizeDatabaseColumnPredecessors,
  normalizeDatabaseDeletedColumns,
  planDatabaseColumnTypeChange,
  TEMPORAL_PROFILE_ROLE_FIELDS,
  untrackDatabaseShardManifestColumn,
} from '@streamlain/database-identity';
import { PublicationError } from '@streamlain/doc-publication-contract';

import { type DatabaseFileCellReviewCopyRow } from './data-source-contracts.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import {
  changeModelDirectory,
  getDatabaseRootReadModel,
} from './data-source-shard-writing.js';
import { isSystemManagedPropertyData } from './data-source-values.js';
import { databasePropertyConverts } from './properties/index.js';
import {
  addProperty,
  getProperty,
  updateProperty,
  updateView,
} from './utils/block-utils.js';

export function propertyAdd(
  this: DatabaseSourceRuntime,
  insertToPosition: InsertToPosition,
  ops?: Parameters<DataSourceBase['propertyAdd']>[1]
): string | undefined {
  if (this._model.props.schemaLocked) return;
  this.captureStorageSync();
  const { type, name, view } = ops ?? {};
  const property = this.propertyMetaGet(
    type ?? propertyPresets.multiSelectPropertyConfig.type
  );
  if (!property) return;
  assertDatabaseColumnShapeWithinLimits({
    source: `Database ${this.databaseId}`,
    columnCount: this.columnCount$.value + 1,
  });
  const id = this._model.store.workspace.idGenerator();
  const mutate = (stage: typeof this._model) => {
    try {
      assertDatabaseColumnShapeWithinLimits({
        source: `Database ${this.databaseId}`,
        columnCount: stage.props.columns.length + 1,
      });
    } catch {
      throw new PublicationError('LIMIT_EXCEEDED');
    }
    if (stage.props.schemaLocked) return;
    const prefix = name ?? 'Column';
    const names = new Set(stage.props.columns.map(column => column.name));
    let nextName = prefix;
    for (let index = 2; names.has(nextName); index++)
      nextName = `${prefix} ${index}`;
    addProperty(stage, insertToPosition, { ...property.create(nextName), id });
    if (view) updateView(stage, view.id, data => view.place(data, id));
    try {
      this.assertFileIndexRootSchemaWithinLimits({}, stage);
    } catch {
      throw new PublicationError('LIMIT_EXCEEDED');
    }
  };
  this.mutateStorageRoot(mutate);
  return id;
}

export function getNormalPropertyAndIndex(
  this: DatabaseSourceRuntime,
  propertyId: string
):
  | {
      column: ColumnDataType<Record<string, unknown>>;
      index: number;
    }
  | undefined {
  // A prepared root exposes staged values; live readers subscribe to changes.
  const columns = getDatabaseRootReadModel(this.storageDoc)
    ? this._model.props.columns
    : this._model.props.columns$.value;
  const index = columns.findIndex(v => v.id === propertyId);
  if (index >= 0) {
    const column = columns[index];
    if (!column) {
      return;
    }
    return {
      column,
      index,
    };
  }
  return;
}

export function getPropertyAndIndex(
  this: DatabaseSourceRuntime,
  propertyId: string
):
  | {
      column: ColumnDataType<Record<string, unknown>>;
      index: number;
    }
  | undefined {
  const result = this.getNormalPropertyAndIndex(propertyId);
  if (result) {
    return result;
  }
  if (this.isFixedProperty(propertyId)) {
    const meta = this.propertyMetaGet(propertyId);
    if (!meta) {
      return;
    }
    const defaultData = meta.config.fixed?.defaultData ?? {};
    return {
      column: {
        data: defaultData,
        id: propertyId,
        type: propertyId,
        name: meta.config.name,
      },
      index: -1,
    };
  }
  return undefined;
}

/** Refuses a column write that grows its options beyond the limit. */
function assertColumnOptionsWithinLimit(
  prev: { id: string; type: string; data?: unknown },
  next: { type?: string; data?: unknown }
): void {
  assertDatabaseColumnOptionsWithinLimit({
    source: 'Database column',
    columnType: next.type ?? prev.type,
    columnId: prev.id,
    current: databaseColumnOptionCount(prev.data),
    next: databaseColumnOptionCount('data' in next ? next.data : prev.data),
  });
}

export function updateSourceProperty(
  this: DatabaseSourceRuntime,
  id: string,
  updater: ColumnUpdater
): string | undefined {
  // Checked before the write is queued, so the caller can tell the user.
  const current = this.getPropertyAndIndex(id)?.column;
  if (current) assertColumnOptionsWithinLimit(current, updater(current));
  // The staged root may differ from the checked one: check it again there.
  const staged: ColumnUpdater = column => {
    const next = updater(column);
    try {
      assertColumnOptionsWithinLimit(column, next);
    } catch {
      throw new PublicationError('LIMIT_EXCEEDED');
    }
    return next;
  };
  this.mutateStorageRoot(stage => {
    const column = stage.props.columns.find(column => column.id === id);
    if (column) updateProperty(stage, id, staged);
    else {
      const fixed = this.getPropertyAndIndex(id);
      if (fixed && fixed.index < 0)
        stage.props.columns = [
          ...stage.props.columns,
          { ...fixed.column, ...staged(fixed.column) },
        ];
    }
    try {
      this.assertFileIndexRootSchemaWithinLimits({}, stage);
    } catch {
      throw new PublicationError('LIMIT_EXCEEDED');
    }
  });
  return id;
}

export function propertyDataGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): Record<string, unknown> {
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return {};
  }
  return result.column.data;
}

export function propertyDataSet(
  this: DatabaseSourceRuntime,
  propertyId: string,
  data: Record<string, unknown>
): void {
  this._runCapture();
  this.updateProperty(propertyId, () =>
    this._model.props.schemaLocked || this.propertyReadonlyGet(propertyId)
      ? {}
      : { data }
  );
}

export function propertyDataTypeGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): TypeInstance | undefined {
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return;
  }
  const { column } = result;
  const meta = this.propertyMetaGet(column.type);
  if (!meta) {
    return;
  }
  return meta.config?.jsonValue.type({
    data: column.data,
    dataSource: this.dataSource,
  });
}

export function propertyDelete(this: DatabaseSourceRuntime, id: string): void {
  if (!this.canMutatePropertySchema(id)) {
    return;
  }
  this.captureStorageSync();
  if (!this._model.props.columns.some(column => column.id === id)) return;
  // Local-first: a device may hold only part of the table and may be
  // offline. Deleting a column therefore changes the table definition only;
  // the cells stay in their row shards and no reader shows them, because
  // every reader walks the defined columns. The deleted-columns entry lets
  // a later write of a row drop its leftover cell (removableDeletedColumnIds).
  this.mutateStorageRoot(stage => {
    const column = stage.props.columns.find(candidate => candidate.id === id);
    if (!column) return;
    stage.props.columns = stage.props.columns.filter(
      candidate => candidate.id !== id
    );
    const deletedAt = Date.now();
    for (const cellKey of databaseColumnDeletedKeys({
      id: column.id,
      cellKey: column.cellKey,
      predecessors: normalizeDatabaseColumnPredecessors(column.predecessors),
    }))
      stage.props.deletedColumns[cellKey] = deletedAt;
  });
}

export function collectDatabaseFileCellReviewCopyRows(
  this: DatabaseSourceRuntime,
  propertyId: string
): DatabaseFileCellReviewCopyRow[] {
  const rows: DatabaseFileCellReviewCopyRow[] = [];
  for (const rowId of this.getLogicalRowIdsSnapshot()) {
    const value = this.cellValueGet(rowId, propertyId);
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      continue;
    }
    for (const [refId, rawItem] of Object.entries(
      value as Record<string, unknown>
    )) {
      if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) {
        continue;
      }
      const blobId =
        typeof (rawItem as { id?: unknown }).id === 'string'
          ? (rawItem as { id: string }).id
          : refId;
      if (!blobId) continue;
      rows.push({ rowId, refId, blobId });
    }
  }
  return rows;
}

export function dispatchDatabaseFileCellReviewCopy(
  this: DatabaseSourceRuntime,
  sourcePropertyId: string,
  targetPropertyId: string,
  rows: DatabaseFileCellReviewCopyRow[]
): void {
  if (rows.length === 0) return;
  dispatchDatabaseFileCellIndexReviewCopy({
    workspaceId: this.workspaceId,
    docId: this.docId,
    databaseId: this.databaseId,
    sourcePropertyId,
    targetPropertyId,
    rows,
  });
}

/**
 * Copies the column definition only. With `copyValues` the copy carries
 * `valuesFrom`; the server then fills the values of all rows, also of rows
 * this device has not loaded.
 */
export function propertyDuplicate(
  this: DatabaseSourceRuntime,
  propertyId: string,
  { copyValues }: PropertyDuplicateOptions
): string | undefined {
  if (
    this.isFixedProperty(propertyId) ||
    this._model.props.schemaLocked ||
    this.propertyReadonlyGet(propertyId)
  ) {
    return;
  }
  assertDatabaseColumnShapeWithinLimits({
    source: `Database ${this.databaseId}`,
    columnCount: this.columnCount$.value + 1,
  });
  const currentSchema = getProperty(this._model, propertyId);
  if (!currentSchema) {
    return;
  }
  // The copy is a new column: an offline merge may have lifted the source
  // above the option limit, but no column starts there (as on the server).
  assertColumnOptionsWithinLimit(
    { id: propertyId, type: currentSchema.type },
    { data: currentSchema.data }
  );
  const fileCellReviewRows =
    copyValues && currentSchema.type === 'attachment'
      ? this.collectDatabaseFileCellReviewCopyRows(propertyId)
      : [];
  this.captureStorageSync();
  // The copy is a new column of the current type: its cells live under its
  // own id, never under the source's cell keys or earlier types.
  const {
    id: copyId,
    valuesFrom: _sourceOfSource,
    cellKey: _sourceCellKey,
    predecessors: _sourcePredecessors,
    ...nonIdProps
  } = currentSchema;
  // Names that differ only in case or surrounding spaces count as taken,
  // as on the server.
  const nameKey = (name: string) => name.trim().toLowerCase();
  const names = new Set(this._model.props.columns.map(v => nameKey(v.name)));
  let index = 1;
  while (names.has(nameKey(`${nonIdProps.name}(${index})`))) {
    index++;
  }
  const schema = {
    ...nonIdProps,
    id: this._model.store.workspace.idGenerator(),
    name: `${nonIdProps.name}(${index})`,
    ...(copyValues ? { valuesFrom: copyId } : {}),
  };
  this.assertFileIndexRootSchemaWithinLimits({
    columns: [...this._model.props.columns, schema],
  });
  this.mutateStorageRoot(stage => {
    addProperty(stage, { before: false, id: propertyId }, schema);
  });
  this.dispatchDatabaseFileCellReviewCopy(
    copyId,
    schema.id,
    fileCellReviewRows
  );
  return schema.id;
}

export function canMutatePropertySchema(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return (
    !this._model.props.schemaLocked &&
    !this.isFixedProperty(propertyId) &&
    !this.propertyReadonlyGet(propertyId) &&
    !this.isTemporalProfileRoleProperty(propertyId)
  );
}

export function isTemporalProfileRoleProperty(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return this._model.props.views.some(view => {
    if (!isTemporalProfileViewType(view.mode)) return false;
    const viewData = view as unknown as Record<string, unknown>;
    return Object.values(TEMPORAL_PROFILE_ROLE_FIELDS).some(fields =>
      fields.some(
        field =>
          this.readTemporalProfileViewPath(viewData, field) === propertyId
      )
    );
  });
}

export function readTemporalProfileViewPath(
  this: DatabaseSourceRuntime,
  viewData: Record<string, unknown>,
  path: string
): unknown {
  return path.split('.').reduce<unknown>((value, key) => {
    if (!value || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, viewData);
}

export function propertyCanDelete(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return this.canMutatePropertySchema(propertyId);
}

export function propertyCanDuplicate(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return (
    this.canMutatePropertySchema(propertyId) &&
    this.columnCount$.value < DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT
  );
}

export function propertyTypeCanSet(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  return this.canMutatePropertySchema(propertyId);
}

export function propertyMetaGet(
  this: DatabaseSourceRuntime,
  type: string
): PropertyMetaConfig | undefined {
  return this.propertyRegistry.value[type];
}

export function propertyNameGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): string {
  if (propertyId === 'type') {
    return 'Block Type';
  }
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return '';
  }
  return result.column.name;
}

export function propertyNameSet(
  this: DatabaseSourceRuntime,
  propertyId: string,
  name: string
): void {
  if (this._model.props.schemaLocked || this.propertyReadonlyGet(propertyId)) {
    return;
  }
  this.assertFileIndexRootSchemaWithinLimits({
    columns: this._model.props.columns.map(column =>
      column.id === propertyId ? { ...column, name } : column
    ),
  });
  this.captureStorageSync();
  this.updateProperty(propertyId, () => ({ name }));
}

export function propertyReadonlyGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): boolean {
  if (
    this._model.props.managedBy === FILE_INDEX_MANAGED_BY &&
    propertyId === 'title'
  ) {
    return true;
  }
  const readonlyProperties = new Set([
    'type',
    'created-time',
    'created-by',
    'updated-time',
    'updated-by',
  ]);
  if (readonlyProperties.has(propertyId)) {
    return true;
  }
  const propertyType = this.propertyTypeGet(propertyId);
  if (propertyType && readonlyProperties.has(propertyType)) {
    return true;
  }
  return isSystemManagedPropertyData(this.propertyDataGet(propertyId));
}

export function propertyTypeGet(
  this: DatabaseSourceRuntime,
  propertyId: string
): string | undefined {
  if (propertyId === 'type') {
    return 'image';
  }
  const result = this.getPropertyAndIndex(propertyId);
  if (!result) {
    return;
  }
  return result.column.type;
}

export function propertyTypeSet(
  this: DatabaseSourceRuntime,
  propertyId: string,
  toType: string
): void | Promise<void> {
  if (!this.canMutatePropertySchema(propertyId)) {
    return;
  }
  return this.propertyTypeSetSharded(propertyId, toType);
}

export function propertyTypeSetSharded(
  this: DatabaseSourceRuntime,
  propertyId: string,
  toType: string
): void | Promise<void> {
  const meta = this.propertyMetaGet(toType);
  if (!meta) {
    return;
  }
  const currentType = this.propertyTypeGet(propertyId);
  if (!currentType) {
    return;
  }
  const convertFunction = databasePropertyConverts.find(
    v => v.from === currentType && v.to === toType
  )?.convert;
  // The new column data never depends on which rows happen to be loaded:
  // text to options reads the values of every table part, every other
  // change builds its data without them.
  const definition = (
    values: readonly unknown[]
  ): Readonly<Record<string, unknown>> =>
    convertFunction?.(this.propertyDataGet(propertyId) as any, [...values])
      .property ?? meta.config.propertyData.default();
  if (!databaseTypeChangeReadsTableValues(currentType, toType)) {
    changeShardedColumnType.call(
      this,
      propertyId,
      currentType,
      toType,
      definition([])
    );
    return;
  }
  const column = this._model.props.columns.find(
    candidate => candidate.id === propertyId
  );
  const expected = {
    type: currentType,
    cellKey: column && databaseColumnCellKey(column),
  };
  return this.dataSource
    .readTableOptionNames(propertyId, toType)
    .then(names =>
      changeShardedColumnType.call(
        this,
        propertyId,
        currentType,
        toType,
        definition(names),
        expected
      )
    );
}

/** The table columns as a cell key check reads them. */
const cellKeyColumns = (
  columns: readonly {
    id: string;
    cellKey?: string;
    predecessors?: unknown;
  }[]
): DatabaseCellColumn[] =>
  columns.map(column => ({
    id: column.id,
    cellKey: column.cellKey,
    predecessors: normalizeDatabaseColumnPredecessors(column.predecessors),
  }));

/** Writes the table definition only; no row block changes. */
function changeShardedColumnType(
  this: DatabaseSourceRuntime,
  propertyId: string,
  currentType: string,
  toType: string,
  data: Readonly<Record<string, unknown>>,
  /** The column as the table read saw it; a change since takes precedence. */
  expected?: { type: string; cellKey: string | undefined }
): void {
  // A taken cell key refuses the change before anything is written.
  const before = this._model.props.columns.find(
    candidate => candidate.id === propertyId
  );
  // So do more options than a column may offer, e.g. text with more
  // distinct values than the limit.
  if (before)
    assertColumnOptionsWithinLimit(
      before,
      planDatabaseColumnTypeChange(
        cellKeyColumns(this._model.props.columns),
        {
          id: before.id,
          type: before.type,
          data: before.data,
          cellKey: before.cellKey,
          predecessors: normalizeDatabaseColumnPredecessors(
            before.predecessors
          ),
        },
        toType,
        data
      )
    );
  this.captureStorageSync();
  // Local-first: a device may hold only part of the table and may be
  // offline. Changing a column type therefore changes the table definition
  // only; every row keeps its cells under the earlier type's key, and every
  // reader converts them through the column lineage (readDatabaseCell). The
  // earlier key enters the deleted-columns list, so a later write of a row
  // moves a converted value once undo can no longer bring the type back.
  this.mutateStorageRoot(stage => {
    const column = stage.props.columns.find(
      candidate => candidate.id === propertyId
    );
    if (!column) return;
    const fromCellKey = databaseColumnCellKey(column);
    if (
      expected &&
      (column.type !== expected.type || fromCellKey !== expected.cellKey)
    )
      return;
    let change: ReturnType<typeof planDatabaseColumnTypeChange>;
    try {
      change = planDatabaseColumnTypeChange(
        cellKeyColumns(stage.props.columns),
        {
          id: column.id,
          type: column.type,
          data: column.data,
          cellKey: column.cellKey,
          predecessors: normalizeDatabaseColumnPredecessors(
            column.predecessors
          ),
        },
        toType,
        data
      );
      assertColumnOptionsWithinLimit(column, change);
    } catch (error) {
      // A column added since the check took the key, or options added since
      // reach the limit: nothing is written.
      if (
        error instanceof DatabaseColumnCellKeyCollisionError ||
        isDatabaseLimitError(error)
      )
        return;
      throw error;
    }
    // Shards stop vouching for the old-type values of the column; the
    // directory lives in parts, so the stage's directory writer marks them.
    if (currentType !== toType)
      changeModelDirectory(this, stage, manifest =>
        untrackDatabaseShardManifestColumn(manifest, propertyId, {
          from: currentType,
          to: toType,
        })
      );
    updateProperty(stage, propertyId, () => ({
      type: change.type,
      data: change.data,
      cellKey: change.cellKey,
      predecessors: change.predecessors.map(predecessor => ({
        ...predecessor,
        data: { ...predecessor.data },
      })),
    }));
    const listed = stage.props.deletedColumns;
    const next = deletedColumnsAfterTypeChange(
      normalizeDatabaseDeletedColumns(listed),
      fromCellKey,
      change.cellKey,
      Date.now()
    );
    // Entry by entry: peers list their deleted columns concurrently.
    for (const key of Object.keys(listed))
      if (!(key in next)) delete listed[key];
    for (const [key, at] of Object.entries(next))
      if (listed[key] !== at) listed[key] = at;
  });
}
